import type {
    TAddStudents,
    TUpdateStudent,
    TResendSeatEmail,
    TRemoveStudentFromGroup,
    TAddStudentToExam,
    TStudent,
    TValidateOrgStudentsWorkerPayload,
    TValidateOrgStudentsWorkerResult,
    TAddStudentsToGroup,
    TUnarchiveSeats,
    TArchiveSeats,
    TCancelPendingSeatsBulk,
    TResendInvites,
} from '@/store/student/types'
import type { Teach, Study } from '@pocketprep/types'
import { runCloudFunction } from '@/store/ParseUtils'
import { fetchLibLoadable, resetLoadable } from '@/loadingUtils'
import { organizationModule } from '@/store/organization/module'
import { licenseModule } from '@/store/license/module'
import { groupModule } from '@/store/group/module'
import { examModule } from '@/store/exam/module'
import { statsModule } from '@/store/stats/module'
import { studentModule } from '@/store/student/module'
import ValidateOrgStudentsWorker from '@/store/student/workers/validateOrgStudentsWorker?worker'

const addStudentToExam = async (params: {
    organizationId: string
    orgStudentId: string
    orgExamIds: string[]
    customMessage?: string
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('addStudentToExam: Could not find currentOrganization')
    }

    await runCloudFunction<TAddStudentToExam>('addStudentToExam', params)

    resetLoadable(studentModule.state.currentOrgStudents[currentOrganization.objectId])
    resetLoadable(licenseModule.state.currentOrgLicenses[currentOrganization.objectId])
    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])

    await Promise.all([
        fetchCurrentOrgStudents(),
        groupModule.actions.fetchCurrentOrgGroups(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])
}

const addStudentsToGroup = async (params: { orgGroupId: string; orgStudentIds: string[] }) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('addStudentsToGroup: Could not find currentOrganization')
    }

    await runCloudFunction<TAddStudentsToGroup>('addStudentsToGroup', params)

    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])

    await groupModule.actions.fetchCurrentOrgGroups()
}

const removeStudentFromCompositeGroup = async (params: {
    groupCompositeKey: string
    orgStudentId: string
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('removeStudentFromGroup: Could not find currentOrganization')
    }

    const { groupCompositeKey, orgStudentId } = params

    const compositeGroupsForKey = groupModule.getters.getGroupsByGroupCompositeKey()[groupCompositeKey]

    const removeStudentPromises = compositeGroupsForKey?.map(compositeGroup => {
        return runCloudFunction<TRemoveStudentFromGroup>('removeStudentFromGroup', {
            orgGroupId: compositeGroup.objectId,
            orgStudentId,
        })
    })

    if (removeStudentPromises) {
        await Promise.all(removeStudentPromises)
    }

    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])

    await groupModule.actions.fetchCurrentOrgGroups()
}

const resendSeatEmail = async (params: { licenseId: string }) => {
    await runCloudFunction<TResendSeatEmail>('resendSeatEmail', params)
}

const unarchiveSeats = async (params: {
    licenseIds: string[]
    organizationId: string
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('unarchiveSeats: Could not find currentOrganization')
    }

    await runCloudFunction<TUnarchiveSeats>('unarchiveSeats', params)

    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])
    resetLoadable(licenseModule.state.currentOrgLicenses[currentOrganization.objectId])

    await Promise.all([
        groupModule.actions.fetchCurrentOrgGroups(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])
}

const archiveSeats = async (params: {
    licenseIds: string[]
    orgInstructorId: string
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('archiveSeats: Could not find currentOrganization')
    }

    await runCloudFunction<TArchiveSeats>('archiveSeats',
        { ...params, organizationId: currentOrganization.objectId }
    )

    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])
    resetLoadable(licenseModule.state.currentOrgLicenses[currentOrganization.objectId])

    await Promise.all([
        groupModule.actions.fetchCurrentOrgGroups(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])
}

const cancelPendingSeatsBulk = async (params: {
    orgStudentIds: string[]
    examGuids: string[]
    orgExamIds: string[]
    organizationId: string
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('cancelPendingSeatsBulk: Could not find currentOrganization')
    }

    await runCloudFunction<TCancelPendingSeatsBulk>('cancelPendingSeatsBulk', params)

    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])
    resetLoadable(licenseModule.state.currentOrgLicenses[currentOrganization.objectId])

    await Promise.all([
        groupModule.actions.fetchCurrentOrgGroups(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])
}

const resendInvites = async (params: {
    organizationId: string
    invites: { orgStudentId: string; email: string; customMessage?: string }[]
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('resendInvites: Could not find currentOrganization')
    }

    await runCloudFunction<TResendInvites>('resendInvites', params)

    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])
    resetLoadable(licenseModule.state.currentOrgLicenses[currentOrganization.objectId])

    await Promise.all([
        groupModule.actions.fetchCurrentOrgGroups(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])
}

const updateStudent = async (params: {
    email: string
    orgStudentId: string
    customMessage?: string
    examInstructors: { [key: string]: string[] }
    organizationId: string
    displayFirstName?: string
    displayLastName?: string
    resendInviteEmail?: boolean
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('updateStudent: Could not find currentOrganization')
    }

    await runCloudFunction<TUpdateStudent>('updateStudent', params)

    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])
    resetLoadable(licenseModule.state.currentOrgLicenses[currentOrganization.objectId])
    resetLoadable(studentModule.state.currentOrgStudents[currentOrganization.objectId])

    await Promise.all([
        groupModule.actions.fetchCurrentOrgGroups(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
        fetchCurrentOrgStudents(),
    ])
}

const addStudents = async (params: {
    organizationId: string
    orgExamIds: string[]
    orgInstructorId?: string
    emails: string[]
    customMessage?: string
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('addStudents: Could not find currentOrganization')
    }

    await runCloudFunction<TAddStudents>('addStudents', params)

    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])
    resetLoadable(licenseModule.state.currentOrgLicenses[currentOrganization.objectId])
    resetLoadable(studentModule.state.currentOrgStudents[currentOrganization.objectId])

    await Promise.all([
        groupModule.actions.fetchCurrentOrgGroups(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
        fetchCurrentOrgStudents(),
    ])
}

const fetchCurrentOrgStudentUEM = async () => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()
    const currentOrgLicenses = await licenseModule.actions.fetchCurrentOrgLicenses()
    const unarchivedOrgLicenses = currentOrgLicenses.filter(license =>
        license.status !== 'archived' && license.orgStudent?.objectId)
    const unarchivedOrgStudentIdSet = new Set(unarchivedOrgLicenses.map(l => l.orgStudent?.objectId))


    if (!currentOrganization) {
        throw new Error('fetchCurrentOrgStudentUEM: Could not find currentOrganization')
    }

    const currentStudents = await fetchCurrentOrgStudents()
    const unarchivedStudents = Object.values(currentStudents).filter(s => unarchivedOrgStudentIdSet.has(s.objectId))

    return fetchLibLoadable(studentModule, 'currentOrgStudentUEM', currentOrganization.objectId, async () => {
        type TFetchStudentUserExamMetadata = (params: { userIds: string[] }) => Study.Class.UserExamMetadataJSON[]

        const students = unarchivedStudents
        const batchSize = 50
        const batchPromises: Promise<ReturnType<TFetchStudentUserExamMetadata>>[] = []
        for (let i = 0; i < students.length; i += batchSize) {
            const userIdBatch = students.slice(i, i + batchSize)
                .filter((s): s is TStudent & { user: NonNullable<TStudent['user']> } => !!s.user)
            // Batch fetch
            batchPromises.push(runCloudFunction<TFetchStudentUserExamMetadata>(
                'fetchStudentUserExamMetadata',
                { userIds: userIdBatch.map(s => s.user.objectId) }
            ))
        }
        const studentUEM = (await Promise.all(batchPromises)).flat()

        const studentUEMLib = studentUEM.reduce((acc, uem) => {
            const userId = uem.user.objectId
            if (!(userId in acc)) {
                acc[userId] = []
            }
            acc[userId] = [ ...(acc[userId] || []), uem ]

            return acc
        }, {} as { [key: string]: Study.Class.UserExamMetadataJSON[] })


        return studentUEMLib
    })
}

const fetchOrgStudentUEMForStudents = async (studentIds: string[], options?: { forceFetch?: boolean }) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('fetchOrgStudentUEMForStudents: Could not find currentOrganization')
    }

    const orgStudents = await fetchCurrentOrgStudents()
    const students = studentIds.map(studentId => orgStudents[studentId])
    const studentsWithUsers = students.filter(s => s?.user)
    if (studentsWithUsers.length === 0) {
        return {} as { [key: string]: Study.Class.UserExamMetadataJSON[] }
    }

    // Get existing UEM data to check which students already have UEM fetched
    const existingLoadable = studentModule.state.currentOrgStudentUEM[currentOrganization.objectId]
    const existingState = existingLoadable && !(existingLoadable.value instanceof Promise)
        ? existingLoadable.value
        : {} as { [key: string]: Study.Class.UserExamMetadataJSON[] }

    // If forceFetch = true, fetch for all students
    // Otherwise, only fetch for students that don't have any UEMs in state yet
    const isStudentMissingUEMs = (student?: TStudent) => {
        const userId = student?.user?.objectId
        return userId && (!existingState[userId] || existingState[userId].length === 0)
    }
    const studentsToFetch = options?.forceFetch
        ? studentsWithUsers
        : studentsWithUsers.filter(isStudentMissingUEMs)

    if (studentsToFetch.length === 0) {
        return existingState
    }

    return fetchLibLoadable(studentModule, 'currentOrgStudentUEM', currentOrganization.objectId, async () => {
        type TFetchStudentUserExamMetadata = (params: { userIds: string[] }) => Study.Class.UserExamMetadataJSON[]

        const batchSize = 50
        const batchPromises: Promise<ReturnType<TFetchStudentUserExamMetadata>>[] = []
        for (let i = 0; i < studentsToFetch.length; i += batchSize) {
            const userIdBatch = studentsToFetch.slice(i, i + batchSize)
                .map(s => s?.user?.objectId).filter((id): id is string => typeof id === 'string')
            // Batch fetch
            batchPromises.push(runCloudFunction<TFetchStudentUserExamMetadata>(
                'fetchStudentUserExamMetadata',
                { userIds: userIdBatch }
            ))
        }
        const studentUEM = (await Promise.all(batchPromises)).flat()

        // Merge new data with existing data
        const updatedState = { ...existingState }
        studentUEM.forEach(uem => {
            const userId = uem.user.objectId
            if (!(userId in updatedState)) {
                updatedState[userId] = []
            }
            // Keep all previous other UEMs for the user but replace the current UEM
            updatedState[userId] = [
                ...(updatedState[userId] || []).filter(prevUEM => prevUEM.objectId !== uem.objectId),
                uem,
            ]
        })

        return updatedState
    }, true)
}

const fetchCurrentOrgStudentUsers = async () => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        return {}
    }

    const currentStudents = Object.values(await fetchCurrentOrgStudents())
        .filter((s): s is TStudent & { user: NonNullable<TStudent['user']> } => !!s.user)

    return fetchLibLoadable(studentModule, 'currentOrgStudentUsers', currentOrganization.objectId, async () => {
        type TFetchUsers = (params: { userIds: string[] }) => Promise<Study.Class.UserJSON[]>
        const studentUsers: Study.Class.UserJSON[] = await runCloudFunction<TFetchUsers>(
            'fetchStudentUsers',
            { userIds: currentStudents.map(s => s.user.objectId) }
        )

        return studentUsers.reduce((acc, s) => ({
            ...acc,
            [s.objectId]: s,
        }), {} as { [key: string]: Study.Class.UserJSON })
    })
}

const fetchCurrentOrgStudents = async () => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        return {}
    }

    return fetchLibLoadable(studentModule, 'currentOrgStudents', currentOrganization.objectId, async () => {
        type TFetchOrgStudents = (params: {
            organizationId: string
            includePendingStudents?: boolean
        }) => Promise<Teach.Class.OrgStudentJSON[]>

        const students = await runCloudFunction<TFetchOrgStudents>(
            'fetchOrgStudents',
            { organizationId: currentOrganization.objectId, includePendingStudents: true }
        )

        return students.reduce(
            (acc, s) => {
                acc[s.objectId] = s
                return acc
            },
            {} as { [key: string]: TStudent }
        )
    })
}

const fetchCurrentGroupStudents = async () => {
    const currentStudents = await fetchCurrentOrgStudents()
    const currentGroup = await groupModule.actions.fetchCurrentGroup()

    return currentGroup
        ? currentGroup.orgStudents.reduce((acc, studentPointer) => {
            const student = currentStudents[studentPointer.objectId]
            if (student) {
                acc.push(student)
            }
            return acc
        }, [] as TStudent[])
        : []
}

const fetchCurrentStudent = async () => {
    const currentStudentId = studentModule.state.currentStudentId
    const students = await fetchCurrentOrgStudents()
    return currentStudentId && students[currentStudentId] || null
}

const updateCurrentStudentId = async (studentId: string | null) => {
    if (examModule.state.currentStudentCurrentExamId !== null) {
        examModule.state.currentStudentCurrentExamId = null
    }

    studentModule.state.currentStudentId = studentId

    if (studentId !== null) {
        const currentStudentDispatch = fetchCurrentStudent()
        const studentExamsDispatch = examModule.actions.fetchCurrentStudentExams()
        const currentStudentCurrentExamDispatch = examModule.actions.fetchCurrentStudentCurrentExam()
        const groupAggStatsDispatch = statsModule.actions.fetchCurrentGroupAggStats()
        const currentGroupExamDispatch = examModule.actions.fetchCurrentGroupExam()
        const examMetadataDispatch = examModule.actions.fetchExamMetadata()
        const [ currentStudent, currentGroupExam, examMetadata ] = await Promise.all([
            currentStudentDispatch,
            currentGroupExamDispatch,
            examMetadataDispatch,
            studentExamsDispatch,
            currentStudentCurrentExamDispatch,
            groupAggStatsDispatch,
        ])

        const currentGroupExamMetadata = currentGroupExam && examMetadata[currentGroupExam.examMetadata.objectId]
        if (!currentStudent || !currentGroupExam || !currentGroupExamMetadata || !currentStudent.user) {
            return
        }

        await statsModule.actions.fetchBatchStudentStats({
            students: [{ studentId, studentUserId: currentStudent.user.objectId }],
            compositeKey: currentGroupExamMetadata.compositeKey,
        })

        await statsModule.actions.fetchAggregatedStudentStats({
            students: [{ studentId, studentUserId: currentStudent.user.objectId }],
            compositeKey: currentGroupExamMetadata.compositeKey,
        })
    }
}

const validateOrgStudents = async () => {
    await Promise.all([
        studentModule.actions.fetchCurrentOrgStudents(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])
    const currentOrgGroups = Object.values(groupModule.getters.getCurrentOrgGroups())
    const groupsByGroupCompositeKey = groupModule.getters.getGroupsByGroupCompositeKey()
    const groupCompositeKeysByGroupId = groupModule.getters.getGroupCompositeKeysByGroupId()
    const orgExamIdGuidLib = examModule.getters.getOrgExamIdGuidLib()
    const currentOrgStudents = studentModule.getters.getCurrentOrgStudents()
    const orgStudents = Object.values(currentOrgStudents)
    const studentLicenseLib = licenseModule.getters.getStudentLicenseLib()

    const validateOrgStudentsWorker = new ValidateOrgStudentsWorker()
    const workerPayload: TValidateOrgStudentsWorkerPayload = JSON.parse(JSON.stringify({
        currentOrgGroups,
        groupsByGroupCompositeKey,
        groupCompositeKeysByGroupId,
        orgExamIdGuidLib,
        orgStudents,
        studentLicenseLib,
    }))
    validateOrgStudentsWorker.postMessage(workerPayload)
    validateOrgStudentsWorker.onmessage = async (event: MessageEvent<TValidateOrgStudentsWorkerResult>) => {
        const {
            payloadsForStudentsMissingFromGroup,
            payloadsForStudentsToRemove,
        } = event.data
        await Promise.all(payloadsForStudentsMissingFromGroup.map(payload =>
            studentModule.actions.addStudentsToGroup(payload)
        ))

        await Promise.all(payloadsForStudentsToRemove.map(payload =>
            studentModule.actions.removeStudentFromCompositeGroup(payload)
        ))
    }
}

export default {
    addStudentToExam,
    addStudentsToGroup,
    removeStudentFromCompositeGroup,
    resendSeatEmail,
    unarchiveSeats,
    archiveSeats,
    resendInvites,
    cancelPendingSeatsBulk,
    updateStudent,
    addStudents,
    fetchCurrentOrgStudentUEM,
    fetchOrgStudentUEMForStudents,
    fetchCurrentOrgStudentUsers,
    fetchCurrentOrgStudents,
    fetchCurrentGroupStudents,
    fetchCurrentStudent,
    updateCurrentStudentId,
    validateOrgStudents,
}
